import React from 'react';

import { Button, Col, DatePicker, Form, InputNumber, Row, Select } from 'antd';
import { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

import './TransactionsFilter.scss';
import Collapsed from 'shared/Collapsed/Collapsed';

import { GetTransactionsParams, transactionTypes, transactionTypeTitles } from 'features/transactions';


interface FormValues extends Omit<GetTransactionsParams, 'page' | 'pageSize' | 'createdFrom' | 'createdTo'> {
  createdFrom: Dayjs;
  createdTo: Dayjs;
}

type FilterValues = Omit<GetTransactionsParams, 'page' | 'pageSize'>

type Props = {
  onSubmit: (data: FilterValues) => void;
  onReset?: () => void;
};

export const TransactionsFilter = ({ onSubmit, onReset }: Props) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'filters' });
  const [form] = Form.useForm<FormValues>();

  const handleResetClick = () => {
    form.resetFields();
    onReset?.();
  };

  const onFinish = (values: FormValues) => {
    const valuesCopy: FilterValues = {
      ...values,
      userId: values.userId,
      createdFrom: values.createdFrom?.startOf('day').toISOString(),
      createdTo: values.createdTo?.endOf('day').toISOString(),
    };

    onSubmit(valuesCopy);
  };

  return (
    <Collapsed label={t('filter', 'Фильтр')}>
      <Form<FormValues>
        layout="vertical"
        form={form}
        className="filter-form"
        onFinish={onFinish}
      >
        <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
          <Col xs={24} md={12} lg={8}>
            <Form.Item
              label={t('id', 'ID')}
              name="id"
              className="filter-form__item"
            >
              <InputNumber />
            </Form.Item>
            <Form.Item
              label={t('user-id', 'User ID')}
              name="userId"
              className="filter-form__item"
            >
              <InputNumber />
            </Form.Item>
          </Col>
          <Col xs={24} md={12} lg={8}>
            <Form.Item
              label={t('type', 'Тип')}
              name="type"
              className="filter-form__item"
            >
              <Select>
                {transactionTypes.map((type) => (
                  <Select.Option key={type} value={type}>
                    {transactionTypeTitles[type]}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Row gutter={10}>
              <Col span={12}>
                <Form.Item
                  label={t('amountFrom', 'Сумма от')}
                  name="amountFrom"
                  className="filter-form__item"
                >
                  <InputNumber />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={t('amountTo', 'Сумма до')}
                  name="amountTo"
                  className="filter-form__item"
                >
                  <InputNumber />
                </Form.Item>
              </Col>
            </Row>
          </Col>
          <Col xs={24} md={12} lg={8}>
            <Row gutter={10}>
              <Col span={12}>
                <Form.Item
                  label={t('createdFrom', 'Дата создания (с)')}
                  name="createdFrom"
                  className="filter-form__item"
                >
                  <DatePicker placeholder={t('select-date', 'Выберите дату')} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={t('createdTo', 'Дата создания (по)')}
                  name="createdTo"
                  className="filter-form__item"
                >
                  <DatePicker placeholder={t('select-date', 'Выберите дату')} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              className="filter-form__item filter-form__buttons-wrapper"
              label={t('controls', 'Управление')}
            >
              <Button type="primary" className="filter-form__button" htmlType="submit">
                {t('apply', 'Применить')}
              </Button>
              <Button
                className="filter-form__button"
                onClick={handleResetClick}
              >
                {t('reset', 'Сбросить')}
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Collapsed>
  );
};
